import React from 'react'
import { useDispatch } from 'react-redux'

import { addProductToBasket } from '../../../state/actions/webshop/webshop'
import {
  BuyProductButton,
  CardDivider,
  CardFooter,
  Container,
  ContainerDivider,
  Content,
  DescriptionList,
  DesktopHeading,
  Footer,
  Image,
  ListItem,
  MobileHeading,
  Price,
  ProductDescription,
  Title,
} from './ProductDetail.styles'
import Text from '../../../stories/atoms/Text/Text'
import Approve from '../../../stories/atoms/Icons/General/Approve'
import currencyFormatter from '../../../util/currency-formatter/currency-formatter'
import { WebshopPackage, SiteAlias } from '../../../schema/webshop-package'
import { siteName } from '../../../schema/mappers/site'
import { useAppSelector } from '../../../state/reducers/types'
import Breadcrumbs from '../../../stories/molecules/Breadcrumbs/Breadcrumbs'
import Link from '../../../stories/atoms/Link/Link'
import { IOLLogoLarge, IOLLogoSmall } from '../../../stories/atoms/Icons/General/IOLLogo'
import { NVBLogoLarge, NVBLogoSmall } from '../../../stories/atoms/Icons/General/NVBLogo'
import BrandInformation from '../brand-information/BrandInformation'
import { toBase64 } from '../../../util/image'
import Heading from '../../../stories/atoms/Heading/Heading'
import OtherProduct from '../other-product/OtherProduct'
import { Action, Category } from '../../../util/analytics-data-attributes/analyticsAttributes'
import { StyledShoppingCardMessage, StyledShoppingCart } from '../product-card/product-card.style'

type ProductDetailProps = {
  product: WebshopPackage
  otherProduct?: WebshopPackage
  className?: string
}

type PrimaryBrand = 'nationalevacaturebank' | 'intermediair'

export function getPrimaryBrand(sites: SiteAlias[] = []): PrimaryBrand | undefined {
  if (sites.length === 0) return

  if (sites.find(site => site === 'nationalevacaturebank')) {
    return 'nationalevacaturebank'
  } else if (sites.find(site => site === 'intermediair')) {
    return 'intermediair'
  }

  return
}

export function getLargeIcon(brand: PrimaryBrand | undefined): string {
  if (!brand) {
    return ''
  } else {
    if (brand === 'intermediair') {
      return toBase64(<IOLLogoLarge />)
    } else if (brand === 'nationalevacaturebank') {
      return toBase64(<NVBLogoLarge />)
    }
  }

  return ''
}

export function getSmallIcon(brand: PrimaryBrand | undefined): string {
  if (!brand) {
    return ''
  } else {
    if (brand === 'intermediair') {
      return toBase64(<IOLLogoSmall />)
    } else if (brand === 'nationalevacaturebank') {
      return toBase64(<NVBLogoSmall />)
    }
  }

  return ''
}

export function generateTitleForSites(sites: SiteAlias[] | undefined): string {
  if (!sites || sites.length === 0) {
    return '-'
  } else if (sites.length === 1) {
    return siteName(sites[0])
  } else {
    const siteNames = sites.map(site => siteName(site)).join(', ')

    return `Sites (${siteNames})`
  }
}

const ProductDetail: React.FunctionComponent<ProductDetailProps> = ({ product, otherProduct, className }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const dispatch = useDispatch()
  const status = useAppSelector(state => state.webshop.status)

  const onAddToBasket: React.MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault()
    setIsSubmitting(true)

    dispatch(addProductToBasket(product))
  }

  React.useEffect(() => {
    if (status !== 'pending' && isSubmitting) {
      setIsSubmitting(false)
    }
  }, [status])

  const primaryBrand = getPrimaryBrand(product.jobPosting?.publishOn)
  const showProductDescription = product.descriptions.length > 0
  const buttonIsDisabled = status === 'pending' && isSubmitting

  return (
    <>
      <Breadcrumbs>
        <Link href="/webshop">WEBSHOP</Link>
        <span>{generateTitleForSites(product.jobPosting?.publishOn)}</span>
        <span>{product.name}</span>
      </Breadcrumbs>

      <Container className={className} data-testid="webshopProductDetail">
        <DesktopHeading icon={getLargeIcon(primaryBrand)}>
          <Title>{product.name}</Title>
          {product.commercialProductDescription && (
            <Text size="large" light>
              {product.commercialProductDescription}
            </Text>
          )}
        </DesktopHeading>

        <MobileHeading icon={getSmallIcon(primaryBrand)}>
          <Title>{product.name}</Title>
          {product.commercialProductDescription && (
            <Text size="large" light>
              {product.commercialProductDescription}
            </Text>
          )}
        </MobileHeading>

        <Image
          data-testid="productCardImage"
          src={product.listImage ? product.listImage : 'https://via.placeholder.com/160x120'}
          alt={product.name}
        />

        <Content>
          <ProductDescription>
            <Heading>Wat kun je verwachten?</Heading>
            {showProductDescription && (
              <DescriptionList data-testid="descriptions">
                {product.descriptions.map(description => (
                  <ListItem key={description} className="checked">
                    <Approve />
                    <span>{description}</span>
                  </ListItem>
                ))}
              </DescriptionList>
            )}
          </ProductDescription>

          <div>
            <CardDivider />

            <CardFooter>
              <Price>{currencyFormatter(product.price, false)}</Price>

              <div>
                <BuyProductButton
                  onClick={onAddToBasket}
                  isInProgress={buttonIsDisabled}
                  disabled={buttonIsDisabled}
                  dataAnalyticsCategory={Category.ecommerce}
                  dataAnalyticsAction={Action.addToCart}
                  dataAnalyticsLabel={product.name}
                  data-testid="buy-product"
                >
                  <StyledShoppingCart /><StyledShoppingCardMessage>In winkelwagen</StyledShoppingCardMessage>
                </BuyProductButton>
              </div>
            </CardFooter>
          </div>
        </Content>
      </Container>

      {primaryBrand ? (
        <>
          <ContainerDivider />
          <Footer>
            <BrandInformation brand={primaryBrand} />
            {otherProduct ? <OtherProduct product={otherProduct} /> : null}
          </Footer>
        </>
      ) : null}
    </>
  )
}

export default ProductDetail
